<script lang="ts">
import type { DepositBonusOption } from '@/types'
import { DateTime } from 'luxon'
import { type PropType, defineComponent } from 'vue'

export default defineComponent({
  name: 'DepositOptionCard',
  props: {
    option: {
      type: Object as PropType<
        DepositBonusOption & {
          coinCount: number
          silverCoinCount: number
          displayAmount: number
          bonusPercent: number
        }
      >,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select'],
  computed: {
    expiry(): DateTime | null {
      const expiry = this.option?.promotion_details?.expires_at ?? null

      if (expiry) {
        return DateTime.fromISO(expiry)
      }

      return null
    },
  },
  methods: {
    handleClick() {
      this.$emit('select', this.option)
    },
  },
})
</script>

<template>
  <div
    class="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg p-3 text-center hover:cursor-pointer relative"
    :class="{
      'border border-primary bg-primary-lighter': isSelected,
      'border m-[1px] border-slate-300': !isSelected,
      'border border-secondary bg-green-50':
        option.status === 'pending_reservation',
      'option-selected': isSelected,
    }"
    @click="handleClick"
  >
    <!-- Unlocked badge for pending reservations -->
    <span
      v-if="option.status === 'pending_reservation'"
      class="unlocked-badge absolute -top-2 -right-2 bg-secondary text-black text-xs font-bold px-2 py-1 rounded-full shadow-sm"
    >
      Unlocked!
    </span>

    <!-- Amount -->
    <p class="text-2xl font-bold">
      {{ $n(option.displayAmount / 100, 'currency') }}
    </p>

    <!-- Bonus percentage -->
    <p v-if="option.bonusPercent > 0" class="font-bold text-blue-800">
      +{{ $n(option.bonusPercent, 'percent') }} Credit
    </p>

    <!-- Countdown timer for promotions with expiry -->
    <div
      v-if="expiry && option.type === 'promotion'"
      class="flex items-center gap-1 mt-1 whitespace-nowrap"
    >
      <p class="text-xs text-gray-600 inline">Ends in</p>

      <AppCountdown
        :datetime="expiry"
        :show-all-units="true"
        time-class="text-xs text-gray-600 font-bold"
        unit-class="text-xs text-gray-600 font-bold"
        class="gap-1"
      />
    </div>
  </div>
</template>
